// Filters the catalogue in the browser. Values in one filter are OR, different filters are AND.
(function () {
  'use strict';

  // The two boxes are read as the product's own two numbers. The caller checks both are set.
  function exactly(range, min, max) {
    if (!range) {
      return false;
    }
    return range[0] === min && range[1] === max;
  }

  // A band holds a product whose span reaches into it, so an adjustable fitting lists in both.
  function touches(range, min, max) {
    if (!range) {
      return false;
    }
    const from = range[0] === null ? -Infinity : range[0];
    const to = range[1] === null ? Infinity : range[1];
    if (min !== null && to < min) {
      return false;
    }
    if (max !== null && from > max) {
      return false;
    }
    return true;
  }

  // The product's whole span must sit inside the asked range. A 2 to 10W fitting is not a 4W one.
  function within(range, min, max) {
    if (!range) {
      return false;
    }
    const low = range[0];
    const high = range[1];
    // A missing end means no limit on that side, so no asked range can hold it.
    const from = low === null ? -Infinity : low;
    const to = high === null ? Infinity : high;
    if (min !== null && from < min) {
      return false;
    }
    if (max !== null && to > max) {
      return false;
    }
    return true;
  }

  // A group means every value in it, so picking White also picks the two tone whites.
  function expandGroups(facet, picked) {
    if (!facet || !facet.groups || facet.groups.length === 0) {
      return picked;
    }
    const groupKeys = new Set(facet.groups.map((group) => group.key));
    const valueKeys = new Set((facet.values || []).map((value) => value.key));
    const wanted = new Set();
    picked.forEach((key) => {
      // A key can name both a group and a value, so keep the value as well.
      if (valueKeys.has(key)) {
        wanted.add(key);
      }
      if (!groupKeys.has(key)) {
        if (!valueKeys.has(key)) {
          wanted.add(key);
        }
        return;
      }
      (facet.values || []).forEach((value) => {
        if (value.group === key) {
          wanted.add(value.key);
        }
      });
    });
    return wanted;
  }

  function createEngine(index) {
    const facetByKey = new Map();
    index.facets.forEach((facet) => facetByKey.set(facet.key, facet));

    // A product stores each value as a number, so map the value keys to those numbers once.
    const codeByValueKey = new Map();
    index.facets.forEach((facet) => {
      if (facet.kind !== 'list') {
        return;
      }
      const perFacet = new Map();
      (facet.values || []).forEach((value) => {
        perFacet.set(value.key, index.code(value.label));
      });
      codeByValueKey.set(facet.key, perFacet);
    });

    function listCodes(facetKey, pickedKeys) {
      const facet = facetByKey.get(facetKey);
      const wanted = expandGroups(facet, pickedKeys);
      const lookup = codeByValueKey.get(facetKey) || new Map();
      const codes = new Set();
      wanted.forEach((key) => {
        const code = lookup.get(key);
        if (code !== undefined && code !== -1) {
          codes.add(code);
        }
      });
      return codes;
    }

    // An empty set means the picked values are no longer in the catalogue, so nothing matches.
    function matchesList(product, facetKey, codes) {
      if (codes.size === 0) {
        return false;
      }
      const at = index.listAt[facetKey];
      if (at === undefined) {
        return false;
      }
      const values = product.lists[at];
      if (!values || values === 0) {
        return false;
      }
      for (let i = 0; i < values.length; i += 1) {
        if (codes.has(values[i])) {
          return true;
        }
      }
      return false;
    }

    /*
      The finish metafield holds one value per product, which the sync split into singles.
      Joining them back gives the value as the merchant wrote it, so the panel can list
      "White, Black" as its own row the way it did before.
    */
    const comboCache = new Map();
    function comboLabels(facetKey) {
      if (!comboCache.has(facetKey)) {
        const at = index.listAt[facetKey];
        comboCache.set(
          facetKey,
          index.products.map((product) => {
            const values = at === undefined ? null : product.lists[at];
            if (!values || values === 0) {
              return '';
            }
            return values.map((code) => index.text(code)).join(', ');
          })
        );
      }
      return comboCache.get(facetKey);
    }

    function allComboLabels(facetKey) {
      const seen = [];
      const labels = comboLabels(facetKey);
      const known = new Set();
      labels.forEach((label) => {
        if (label && !known.has(label)) {
          known.add(label);
          seen.push(label);
        }
      });
      return seen;
    }

    // A band is one row of a banded filter, either a span or a word the display spec holds.
    function matchesBand(product, rangeKey, band) {
      if (band.contains) {
        for (let i = 0; i < band.specs.length; i += 1) {
          const specAt = index.specAt[band.specs[i]];
          if (specAt === undefined) {
            continue;
          }
          if (index.text(product.specs[specAt]).toLowerCase().indexOf(band.contains) !== -1) {
            return true;
          }
        }
        return false;
      }
      const at = index.rangeAt[rangeKey];
      if (at === undefined) {
        return false;
      }
      const range = product.ranges[at];
      if (!range || range === 0) {
        return false;
      }
      const low = band.min === undefined ? null : band.min;
      const high = band.max === undefined ? null : band.max;
      return touches(range, low, high);
    }

    function matchesAnyBand(product, rangeKey, bands) {
      for (let i = 0; i < bands.length; i += 1) {
        if (matchesBand(product, rangeKey, bands[i])) {
          return true;
        }
      }
      return false;
    }

    /*
      Shopify matches a tag without caring about case, and this catalogue holds both
      Interior and interior, so a wanted tag carries every spelling the strings hold.
    */
    const codesByLower = new Map();
    function codesForTag(tag) {
      if (codesByLower.size === 0) {
        index.strings.forEach((value, at) => {
          const key = String(value).toLowerCase();
          const found = codesByLower.get(key);
          if (found) {
            found.push(at);
          } else {
            codesByLower.set(key, [at]);
          }
        });
      }
      return codesByLower.get(String(tag).toLowerCase()) || [];
    }

    // A wide query names product tags, and every one of them has to be present.
    function matchesTags(product, groups) {
      for (let i = 0; i < groups.length; i += 1) {
        const codes = groups[i];
        let found = false;
        for (let j = 0; j < codes.length; j += 1) {
          if (product.tags.indexOf(codes[j]) !== -1) {
            found = true;
            break;
          }
        }
        if (!found) {
          return false;
        }
      }
      return true;
    }

    function matchesRange(product, facetKey, bounds, exact) {
      const at = index.rangeAt[facetKey];
      if (at === undefined) {
        return false;
      }
      const range = product.ranges[at];
      if (!range || range === 0) {
        return false;
      }
      // One box names one end only, so there is no pair to match and the row reads as a range.
      const bothGiven = bounds.min !== null && bounds.max !== null;
      return exact && bothGiven
        ? exactly(range, bounds.min, bounds.max)
        : within(range, bounds.min, bounds.max);
    }

    /*
      query = {
        collection: 'interior-spots' | null,
        lists: { finish: Set('white'), ... },
        ranges: { watt: { min: 4.4, max: 5.5 }, ... },
        bands: { beam_angle: { rangeKey: 'beam_angle', chosen: [{ min: 16, max: 30 }] } },
        unionScope: true,
        exactRangeKeys: Set('watt'),
        tags: Set('Interior', 'Spots'),
        search: 'text'
      }
    */
    function prepare(query) {
      const lists = [];
      Object.keys(query.lists || {}).forEach((facetKey) => {
        const picked = query.lists[facetKey];
        if (picked && picked.size > 0) {
          lists.push([facetKey, listCodes(facetKey, picked)]);
        }
      });

      const ranges = [];
      Object.keys(query.ranges || {}).forEach((facetKey) => {
        const bounds = query.ranges[facetKey];
        if (bounds && (bounds.min !== null || bounds.max !== null)) {
          ranges.push([facetKey, bounds]);
        }
      });

      // A tag the catalogue never used has no codes, so it empties the result.
      const tagCodes = [];
      (query.tags || new Set()).forEach((tag) => tagCodes.push(codesForTag(tag)));

      const combos = [];
      Object.keys(query.combos || {}).forEach((facetKey) => {
        const picked = query.combos[facetKey];
        if (picked && picked.size > 0) {
          combos.push([facetKey, picked]);
        }
      });

      // Bands picked in one filter are OR, the same as list values.
      const bands = [];
      Object.keys(query.bands || {}).forEach((facetKey) => {
        const picked = query.bands[facetKey];
        if (picked && picked.rangeKey && picked.chosen && picked.chosen.length > 0) {
          bands.push([facetKey, picked.rangeKey, picked.chosen]);
        }
      });

      const collectionCode = query.collection ? index.code(query.collection) : -1;
      const search = (query.search || '').trim().toLowerCase();
      return {
        lists: lists,
        ranges: ranges,
        combos: combos,
        bands: bands,
        exactKeys: query.exactRangeKeys || null,
        tagCodes: tagCodes,
        collectionCode: collectionCode,
        unionScope: query.unionScope === true,
        search: search
      };
    }

    function matches(product, plan, skipFacetKey, at) {
      /*
        A collection holds products its wide query misses, so the two are added together.
        Scoping to the tags alone would drop those, and the panel would lose their values.
      */
      if (plan.unionScope) {
        const inCollection =
          plan.collectionCode !== -1 && product.collections.has(plan.collectionCode);
        const inTags = plan.tagCodes.length > 0 && matchesTags(product, plan.tagCodes);
        if (!inCollection && !inTags) {
          return false;
        }
      } else {
        if (plan.collectionCode !== -1 && !product.collections.has(plan.collectionCode)) {
          return false;
        }
        if (plan.tagCodes.length > 0 && !matchesTags(product, plan.tagCodes)) {
          return false;
        }
      }
      for (let i = 0; i < plan.combos.length; i += 1) {
        const entry = plan.combos[i];
        if (entry[0] === skipFacetKey) {
          continue;
        }
        if (!entry[1].has(comboLabels(entry[0])[at])) {
          return false;
        }
      }
      for (let i = 0; i < plan.bands.length; i += 1) {
        const entry = plan.bands[i];
        if (entry[0] === skipFacetKey) {
          continue;
        }
        if (!matchesAnyBand(product, entry[1], entry[2])) {
          return false;
        }
      }
      for (let i = 0; i < plan.lists.length; i += 1) {
        const entry = plan.lists[i];
        if (entry[0] === skipFacetKey) {
          continue;
        }
        if (!matchesList(product, entry[0], entry[1])) {
          return false;
        }
      }
      for (let i = 0; i < plan.ranges.length; i += 1) {
        const entry = plan.ranges[i];
        if (entry[0] === skipFacetKey) {
          continue;
        }
        const exact = plan.exactKeys ? plan.exactKeys.has(entry[0]) : false;
        if (!matchesRange(product, entry[0], entry[1], exact)) {
          return false;
        }
      }
      if (plan.search !== '') {
        const title = (product.title || '').toLowerCase();
        const sku = (product.sku || '').toLowerCase();
        if (title.indexOf(plan.search) === -1 && sku.indexOf(plan.search) === -1) {
          return false;
        }
      }
      return true;
    }

    function run(query) {
      const plan = prepare(query);
      const hits = [];
      for (let i = 0; i < index.products.length; i += 1) {
        const product = index.products[i];
        if (matches(product, plan, null, i)) {
          hits.push(product);
        }
      }
      return hits;
    }

    // Count a product once per group, even when it has more than one value in that group.
    function groupKeyByCode(facet) {
      const lookup = codeByValueKey.get(facet.key) || new Map();
      const byCode = new Map();
      (facet.values || []).forEach((value) => {
        if (!value.group) {
          return;
        }
        const code = lookup.get(value.key);
        if (code !== undefined && code !== -1) {
          byCode.set(code, value.group);
        }
      });
      return byCode;
    }

    function countGroups(facet, plan, at) {
      if (!facet.groups || facet.groups.length === 0) {
        return {};
      }
      const byCode = groupKeyByCode(facet);
      const totals = {};
      facet.groups.forEach((group) => {
        totals[group.key] = 0;
      });
      for (let i = 0; i < index.products.length; i += 1) {
        const product = index.products[i];
        if (!matches(product, plan, facet.key, i)) {
          continue;
        }
        const values = product.lists[at];
        if (!values || values === 0) {
          continue;
        }
        const hit = new Set();
        for (let v = 0; v < values.length; v += 1) {
          const groupKey = byCode.get(values[v]);
          if (groupKey !== undefined) {
            hit.add(groupKey);
          }
        }
        hit.forEach((groupKey) => {
          if (totals[groupKey] !== undefined) {
            totals[groupKey] += 1;
          }
        });
      }
      return totals;
    }

    // Each value is counted against every filter but its own, so its siblings stay visible.
    function counts(query) {
      const result = {};
      const groupTotals = {};
      const plan = prepare(query);

      index.facets.forEach((facet) => {
        if (facet.kind !== 'list') {
          return;
        }
        const at = index.listAt[facet.key];
        if (at === undefined) {
          return;
        }
        const perCode = new Map();
        for (let i = 0; i < index.products.length; i += 1) {
          const product = index.products[i];
          if (!matches(product, plan, facet.key, i)) {
            continue;
          }
          const values = product.lists[at];
          if (!values || values === 0) {
            continue;
          }
          for (let v = 0; v < values.length; v += 1) {
            perCode.set(values[v], (perCode.get(values[v]) || 0) + 1);
          }
        }
        const byValueKey = {};
        const lookup = codeByValueKey.get(facet.key) || new Map();
        lookup.forEach((code, valueKey) => {
          byValueKey[valueKey] = perCode.get(code) || 0;
        });
        result[facet.key] = byValueKey;
        groupTotals[facet.key] = countGroups(facet, plan, at);
      });

      index.facets.forEach((facet) => {
        if (facet.kind !== 'range') {
          return;
        }
        const at = index.rangeAt[facet.key];
        if (at === undefined) {
          return;
        }
        let low = null;
        let high = null;
        let found = 0;
        for (let i = 0; i < index.products.length; i += 1) {
          const product = index.products[i];
          if (!matches(product, plan, facet.key, i)) {
            continue;
          }
          const range = product.ranges[at];
          if (!range || range === 0) {
            continue;
          }
          found += 1;
          if (range[0] !== null) {
            low = low === null ? range[0] : Math.min(low, range[0]);
          }
          if (range[1] !== null) {
            high = high === null ? range[1] : Math.max(high, range[1]);
          }
        }
        // The span still reachable, which the inputs show as a hint.
        result[facet.key] = { min: low, max: high, count: found };
      });

      result.groups = groupTotals;
      return result;
    }

    // The values a finish style filter lists, counted against every filter but its own.
    function comboValues(query, facetKey) {
      const plan = prepare(query);
      const labels = comboLabels(facetKey);
      const totals = new Map();
      for (let i = 0; i < index.products.length; i += 1) {
        if (!matches(index.products[i], plan, facetKey, i)) {
          continue;
        }
        const label = labels[i];
        if (label) {
          totals.set(label, (totals.get(label) || 0) + 1);
        }
      }
      return Array.from(totals.entries()).map((entry) => ({ label: entry[0], count: entry[1] }));
    }

    // Counted against every filter but this one, so a picked band still shows its siblings.
    function countBands(query, facetKey, rangeKey, bands) {
      const plan = prepare(query);
      const totals = bands.map(() => 0);
      for (let i = 0; i < index.products.length; i += 1) {
        const product = index.products[i];
        if (!matches(product, plan, facetKey, i)) {
          continue;
        }
        for (let b = 0; b < bands.length; b += 1) {
          if (matchesBand(product, rangeKey, bands[b])) {
            totals[b] += 1;
          }
        }
      }
      return totals;
    }

    const SORTS = {
      'title-ascending': function (a, b) {
        return (a.title || '').localeCompare(b.title || '');
      },
      'title-descending': function (a, b) {
        return (b.title || '').localeCompare(a.title || '');
      },
      'price-ascending': function (a, b) {
        return (a.price === null ? Infinity : a.price) - (b.price === null ? Infinity : b.price);
      },
      'price-descending': function (a, b) {
        return (b.price === null ? -Infinity : b.price) - (a.price === null ? -Infinity : a.price);
      }
    };

    function sort(products, key) {
      const compare = SORTS[key];
      return compare ? products.slice().sort(compare) : products;
    }

    function page(products, pageNumber, perPage) {
      const size = perPage > 0 ? perPage : 24;
      const pages = Math.max(1, Math.ceil(products.length / size));
      const current = Math.min(Math.max(1, pageNumber || 1), pages);
      const from = (current - 1) * size;
      return {
        items: products.slice(from, from + size),
        page: current,
        pages: pages,
        total: products.length,
        from: products.length === 0 ? 0 : from + 1,
        to: Math.min(from + size, products.length)
      };
    }

    return {
      facetByKey: facetByKey,
      run: run,
      counts: counts,
      countBands: countBands,
      comboValues: comboValues,
      allComboLabels: allComboLabels,
      sort: sort,
      page: page,
      within: within,
      touches: touches,
      exactly: exactly
    };
  }

  window.SACatalogEngine = { create: createEngine, within: within, touches: touches, exactly: exactly };
})();
